"use client";

import { useState, useRef, type FormEvent, type RefObject } from "react";
import dynamic from "next/dynamic";
import { calculateMatching } from "@/lib/api/kundali";
import type { PersonInput, MatchingResponse } from "@/lib/api/kundali";
import type { PlaceResult } from "@/lib/api/misc";
import Input from "@ishvaram/ui/Input";
import Button from "@ishvaram/ui/Button";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import { trackCalculatorUsed, trackMatchingCompleted } from "@/lib/analytics";
import { MatchingResultsPanel } from "@/components/matching/MatchingResultsPanel";

const AIAstrologerCTA = dynamic(() => import("@/components/AIAstrologerCTA"), {
  ssr: false,
  loading: () => null,
});

interface FormState {
  name: string;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  place: PlaceResult | null;
}

const empty = (): FormState => ({
  name: "",
  birthDate: "",
  birthTime: "",
  birthPlace: "",
  place: null,
});

function PersonPanel({
  label,
  icon,
  data,
  onChange,
}: {
  label: string;
  icon: string;
  data: FormState;
  onChange: (patch: Partial<FormState>) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const id = label.toLowerCase().replace(/\s+/g, "-");

  return (
    <div className="card-sacred p-5 sm:p-6 space-y-4 flex-1">
      <h3
        className="text-[16px] font-bold flex items-center gap-2"
        style={{ color: "var(--ish-dark)", fontFamily: "PT Serif, Georgia, serif" }}
      >
        <span>{icon}</span> {label}
      </h3>

      <Input
        id={`name-${id}`}
        type="text"
        required
        label="Full Name"
        value={data.name}
        onChangeText={(v) => onChange({ name: v })}
        placeholder="Enter full name"
        autoComplete="name"
        autoCapitalize="words"
      />

      <Input
        id={`date-${id}`}
        type="date"
        required
        label="Birth Date"
        value={data.birthDate}
        onChangeText={(v) => onChange({ birthDate: v })}
        min="1900-01-01"
        max={today}
      />

      <Input
        id={`time-${id}`}
        type="time"
        label="Birth Time (optional)"
        value={data.birthTime}
        onChangeText={(v) => onChange({ birthTime: v })}
      />

      <div>
        <label
          htmlFor={`place-${id}`}
          className="block text-[13px] font-semibold mb-2"
          style={{ color: "var(--ish-dark)" }}
        >
          Birth Place
        </label>
        <PlaceAutocomplete
          id={`place-${id}`}
          value={data.birthPlace}
          onChange={(v) => onChange({ birthPlace: v, place: null })}
          onSelect={(p) => onChange({ birthPlace: p.display, place: p })}
        />
      </div>
    </div>
  );
}

function toPersonInput(f: FormState): PersonInput {
  return {
    name: f.name,
    birth_date: f.birthDate,
    birth_time: f.birthTime || null,
    birth_place: f.birthPlace,
    ...(f.place && {
      latitude: f.place.latitude,
      longitude: f.place.longitude,
      timezone: f.place.timezone,
    }),
  } as PersonInput;
}

export default function GunMilanClient({
  language = "en",
}: {
  language?: "en" | "hi";
} = {}) {
  const [boy, setBoy] = useState<FormState>(empty());
  const [girl, setGirl] = useState<FormState>(empty());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MatchingResponse | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  async function handleCalculate(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await calculateMatching({
        person_a: toPersonInput(boy),
        person_b: toPersonInput(girl),
      });
      setResult(res);
      trackCalculatorUsed({ calculator: "gun-milan" });
      trackMatchingCompleted({ score: res.total_score ?? 0 });
      setTimeout(
        () => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
        100
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setBoy(empty());
    setGirl(empty());
    setResult(null);
    setError(null);
  }

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12">
      {!result ? (
        <GunMilanForm
          boy={boy}
          girl={girl}
          loading={loading}
          error={error}
          onSubmit={handleCalculate}
          onBoyChange={(patch) => setBoy((prev) => ({ ...prev, ...patch }))}
          onGirlChange={(patch) => setGirl((prev) => ({ ...prev, ...patch }))}
        />
      ) : (
        <GunMilanResults
          result={result}
          boy={boy}
          girl={girl}
          resultsRef={resultsRef}
          onReset={handleReset}
          language={language}
        />
      )}
    </div>
  );
}

function GunMilanForm({
  boy,
  girl,
  loading,
  error,
  onSubmit,
  onBoyChange,
  onGirlChange,
}: {
  boy: FormState;
  girl: FormState;
  loading: boolean;
  error: string | null;
  onSubmit: (event: FormEvent) => void;
  onBoyChange: (patch: Partial<FormState>) => void;
  onGirlChange: (patch: Partial<FormState>) => void;
}) {
  return (
    <form onSubmit={onSubmit} noValidate>
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <PersonPanel label="Boy (वर)" icon="♂" data={boy} onChange={onBoyChange} />
        <PersonPanel label="Girl (वधू)" icon="♀" data={girl} onChange={onGirlChange} />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-4 text-[13px] rounded-lg p-3.5 text-center"
          style={{
            color: "var(--color-error-text)",
            backgroundColor: "var(--color-error-bg)",
            border: "1px solid var(--color-error-tint)",
          }}
        >
          {error}
        </div>
      )}

      <Button
        type="submit"
        onPress={() => {}}
        disabled={loading}
        loading={loading}
        variant="primary"
        title={loading ? "Calculating Gun Milan..." : "Calculate Gun Milan →"}
        className="w-full py-4 text-[15px] font-semibold"
      />

      <p className="mt-3 text-center text-[12px] text-ish-text-faint">
        36-point Ashtakoot score &middot; All 8 Kootas &middot; Mangal Dosha check &middot; 100%
        free
      </p>
    </form>
  );
}

function GunMilanResults({
  result,
  boy,
  girl,
  resultsRef,
  onReset,
  language,
}: {
  result: MatchingResponse;
  boy: FormState;
  girl: FormState;
  resultsRef: RefObject<HTMLDivElement | null>;
  onReset: () => void;
  language: "en" | "hi";
}) {
  return (
    <div ref={resultsRef} className="space-y-6">
      <NamesBanner boyName={boy.name} girlName={girl.name} />
      <MatchingResultsPanel
        surface="gun-milan"
        result={result}
        personAData={toPersonInput(boy)}
        personBData={toPersonInput(girl)}
        defaultLanguage={language}
        onReset={onReset}
        showDetailedReport={false}
      />
      <AIAstrologerCTA context="matching" />
    </div>
  );
}

function NamesBanner({ boyName, girlName }: { boyName: string; girlName: string }) {
  return (
    <div
      className="card-sacred p-4 flex items-center justify-center gap-3 text-center"
      style={{ borderColor: "var(--color-accent-border)" }}
    >
      <span className="text-[16px] font-bold text-ish-dark">{boyName}</span>
      <span style={{ color: "var(--ish-accent)" }}>♥</span>
      <span className="text-[16px] font-bold text-ish-dark">{girlName}</span>
    </div>
  );
}
